// src/components/Call/AudioCall.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  connectSocket,
  joinRoom,
  onOffer,
  onAnswer,
  onCandidate,
  sendOffer,
  sendAnswer,
  sendCandidate,
} from "../../socket/signalingClient";

export default function AudioCall({ roomId, userId, onClose, ringtoneRef }) {
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);

  const [status, setStatus] = useState("connecting");

  useEffect(() => {
    if (!roomId || !userId) return;

    console.log("🎧 AudioCall started", roomId, userId);

    // 🔔 Play ringtone
    if (ringtoneRef?.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    }

    const init = async () => {
      connectSocket(userId);
      joinRoom(roomId, userId);

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pcRef.current = pc;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      if (localAudioRef.current) localAudioRef.current.srcObject = stream;

      // Remote audio
      pc.ontrack = (e) => {
        console.log("🔊 Remote audio received");
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = e.streams[0];
        setStatus("connected");
        if (ringtoneRef?.current) ringtoneRef.current.pause();
      };

      // ICE candidates
      pc.onicecandidate = (e) => {
        if (e.candidate) sendCandidate(roomId, e.candidate);
      };

      // Offer received
      onOffer(async (offer) => {
        console.log("📥 Offer received");
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        // If we are callee, create answer
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendAnswer(roomId, answer);
      });

      // Answer received
      onAnswer(async (answer) => {
        console.log("📥 Answer received");
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      });

      // ICE candidate received
      onCandidate(async (candidate) => {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.error("ICE candidate error", e);
        }
      });

      // ✅ Offer creation logic
      // If caller (first in room) OR if callee mounts without remoteDescription yet
      const isCaller = roomId.split("_")[0] === userId;
      if (isCaller || !pc.currentRemoteDescription) {
        console.log("📤 Creating audio offer");
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendOffer(roomId, offer);
      }
    };

    init();

    return () => {
      console.log("🧹 Closing AudioCall");
      if (pcRef.current) pcRef.current.close();
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
      if (ringtoneRef?.current) ringtoneRef.current.pause();
    };
  }, [roomId, userId, ringtoneRef]);

  return (
    <div className="audio-call-container">
      <h3>🎧 Audio Call</h3>
      <p>Status: {status}</p>

      <audio ref={localAudioRef} autoPlay muted />
      <audio ref={remoteAudioRef} autoPlay />

      <div style={{ marginTop: 10 }}>
        <button onClick={onClose}>End Call</button>
      </div>
    </div>
  );
}
